import { toast } from "react-toastify";
import { Alert, Collapse, Form, Space, Tabs, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import InputField from "../../../components/common/fields/InputField";
import SelectField from "../../../components/common/fields/SelectField";
import SettingsPanel, { SwitchSetting } from "../../../components/common/SettingsPanel";
import Card from "../../../components/common/Card";
import { pathAPI } from "../../../utils/pathApi";
import WebhookClients from "./WebhookClients";

interface AiChatSettingsValues {
  enabled: boolean;
  bubble_enabled: boolean;
  read_only_tools_enabled: boolean;
  booking_enabled: boolean;
  enabled_country_ids: number[];
  enabled_platforms: string[];
  enabled_app_versions: string[];
  disabled_message: string;
}

interface AiChatSettingsFormValues
  extends Omit<AiChatSettingsValues, "enabled_country_ids" | "enabled_app_versions"> {
  enabled_country_ids: string;
  enabled_app_versions: string;
}

interface OwnerAiChatSettingsValues {
  enabled: boolean;
  voice_enabled: boolean;
  disabled_message: string;
}

const DISABLED_MESSAGE_MAX_LENGTH = 240;

const buildDefaultSettings = (disabledMessage: string): AiChatSettingsValues => ({
  enabled: false,
  bubble_enabled: false,
  read_only_tools_enabled: true,
  booking_enabled: false,
  enabled_country_ids: [],
  enabled_platforms: [],
  enabled_app_versions: [],
  disabled_message: disabledMessage,
});

const buildDefaultOwnerSettings = (disabledMessage: string): OwnerAiChatSettingsValues => ({
  enabled: true,
  voice_enabled: true,
  disabled_message: disabledMessage,
});

const unwrap = (response: any) => response?.data?.data ?? response?.data;

const toFormValues = (settings: AiChatSettingsValues): AiChatSettingsFormValues => ({
  ...settings,
  enabled_country_ids: settings.enabled_country_ids?.join(",") ?? "",
  enabled_app_versions: settings.enabled_app_versions?.join(",") ?? "",
});

const toRequestValues = (values: AiChatSettingsFormValues): AiChatSettingsValues => ({
  ...values,
  enabled_country_ids: values.enabled_country_ids
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(Number)
    .filter((item) => Number.isFinite(item) && item > 0),
  enabled_app_versions: values.enabled_app_versions
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean),
});

const AiChatSettings = () => {
  const { t } = useTranslation();
  const [userForm] = Form.useForm<AiChatSettingsFormValues>();
  const [ownerForm] = Form.useForm<OwnerAiChatSettingsValues>();
  const [userLoading, setUserLoading] = useState(false);
  const [ownerLoading, setOwnerLoading] = useState(false);

  const defaultMessage = t("ai_chat.default_disabled_message");
  const defaultSettings = useMemo(() => buildDefaultSettings(defaultMessage), [defaultMessage]);
  const defaultOwnerSettings = useMemo(() => buildDefaultOwnerSettings(defaultMessage), [defaultMessage]);

  const userSwitches: SwitchSetting[] = [
    { name: "enabled", label: t("ai_chat.user.switch.enabled"), description: t("ai_chat.user.switch.enabled_desc") },
    { name: "bubble_enabled", label: t("ai_chat.user.switch.bubble"), description: t("ai_chat.user.switch.bubble_desc") },
    { name: "read_only_tools_enabled", label: t("ai_chat.user.switch.read_only"), description: t("ai_chat.user.switch.read_only_desc") },
    { name: "booking_enabled", label: t("ai_chat.user.switch.booking"), description: t("ai_chat.user.switch.booking_desc") },
  ];

  const ownerSwitches: SwitchSetting[] = [
    { name: "enabled", label: t("ai_chat.user.switch.enabled"), description: t("ai_chat.owner.switch.enabled_desc") },
    { name: "voice_enabled", label: t("ai_chat.owner.switch.voice"), description: t("ai_chat.owner.switch.voice_desc") },
  ];

  const platformOptions = useMemo(
    () => [
      { label: t("ai_chat.platform.ios"), value: "ios" },
      { label: t("ai_chat.platform.android"), value: "android" },
    ],
    [t]
  );

  const fetchUserSettings = useCallback(async () => {
    setUserLoading(true);
    try {
      const response = await axiosInstance.get(`${pathAPI.aiChat}/settings`);
      userForm.setFieldsValue(toFormValues({ ...defaultSettings, ...unwrap(response) }));
    } catch (error) {
      toast.error(t("ai_chat.user.load_error"));
    } finally {
      setUserLoading(false);
    }
  }, [userForm, defaultSettings, t]);

  const saveUserSettings = async (values: AiChatSettingsFormValues) => {
    setUserLoading(true);
    try {
      const response = await axiosInstance.put(
        `${pathAPI.aiChat}/settings`,
        toRequestValues(values)
      );
      userForm.setFieldsValue(toFormValues({ ...defaultSettings, ...unwrap(response) }));
      toast.success(t("ai_chat.user.save_ok"));
    } catch (error) {
      toast.error(t("ai_chat.user.save_error"));
    } finally {
      setUserLoading(false);
    }
  };

  const fetchOwnerSettings = useCallback(async () => {
    setOwnerLoading(true);
    try {
      const response = await axiosInstance.get(`${pathAPI.aiChat}/owner-settings`);
      ownerForm.setFieldsValue({ ...defaultOwnerSettings, ...unwrap(response) });
    } catch (error) {
      toast.error(t("ai_chat.owner.load_error"));
    } finally {
      setOwnerLoading(false);
    }
  }, [ownerForm, defaultOwnerSettings, t]);

  const saveOwnerSettings = async (values: OwnerAiChatSettingsValues) => {
    setOwnerLoading(true);
    try {
      const response = await axiosInstance.put(
        `${pathAPI.aiChat}/owner-settings`,
        values
      );
      ownerForm.setFieldsValue({ ...defaultOwnerSettings, ...unwrap(response) });
      toast.success(t("ai_chat.owner.save_ok"));
    } catch (error) {
      toast.error(t("ai_chat.owner.save_error"));
    } finally {
      setOwnerLoading(false);
    }
  };

  useEffect(() => {
    fetchUserSettings();
    fetchOwnerSettings();
  }, [fetchUserSettings, fetchOwnerSettings]);

  const tabItems = [
    {
      key: "user",
      label: t("ai_chat.tab.user"),
      children: (
        <Form
          form={userForm}
          layout="vertical"
          initialValues={toFormValues(defaultSettings)}
          onFinish={saveUserSettings}
          disabled={userLoading}
          style={{ marginTop: 8 }}
        >
          <SettingsPanel
            title={t("ai_chat.user.panel_title")}
            description={t("ai_chat.user.panel_desc")}
            loading={userLoading}
            onReload={fetchUserSettings}
            switches={userSwitches}
          >
            <Form.Item
              name="enabled_country_ids"
              label={t("ai_chat.user.country_ids")}
              extra={t("ai_chat.user.country_ids_extra")}
            >
              <InputField placeholder="1,2,3" />
            </Form.Item>
            <Form.Item
              name="enabled_platforms"
              label={t("ai_chat.user.platforms")}
              extra={t("ai_chat.user.platforms_extra")}
            >
              <SelectField multiple options={platformOptions} placeholder={t("ai_chat.user.platforms_ph")} />
            </Form.Item>
            <Form.Item
              name="enabled_app_versions"
              label={t("ai_chat.user.versions")}
              extra={t("ai_chat.user.versions_extra")}
            >
              <InputField placeholder="1.2.3,1.2.4" />
            </Form.Item>
            <Form.Item name="disabled_message" label={t("ai_chat.disabled_message")}>
              <InputField multiline minRows={3} inputProps={{ maxLength: DISABLED_MESSAGE_MAX_LENGTH }} />
            </Form.Item>
          </SettingsPanel>
        </Form>
      ),
    },
    {
      key: "so",
      label: t("ai_chat.tab.so"),
      children: (
        <Form
          form={ownerForm}
          layout="vertical"
          initialValues={defaultOwnerSettings}
          onFinish={saveOwnerSettings}
          disabled={ownerLoading}
          style={{ marginTop: 8 }}
        >
          <SettingsPanel
            title={t("ai_chat.owner.panel_title")}
            description={t("ai_chat.owner.panel_desc")}
            loading={ownerLoading}
            onReload={fetchOwnerSettings}
            switches={ownerSwitches}
          >
            <Form.Item name="disabled_message" label={t("ai_chat.disabled_message")}>
              <InputField multiline minRows={3} inputProps={{ maxLength: DISABLED_MESSAGE_MAX_LENGTH }} />
            </Form.Item>
          </SettingsPanel>
        </Form>
      ),
    },
    {
      key: "webhook",
      label: t("ai_chat.tab.webhook"),
      children: <WebhookClients />,
    },
  ];

  return (
    <Card className="user">
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Space direction="vertical" size={4}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t("ai_chat.title")}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t("ai_chat.subtitle")}
          </Typography.Text>
        </Space>

        <Alert
          type="info"
          showIcon
          message={t("ai_chat.env_alert")}
        />

        <Collapse
          size="small"
          items={[
            {
              key: "hierarchy",
              label: t("ai_chat.how_title"),
              children: (
                <Typography.Text type="secondary">
                  {t("ai_chat.how_global")}<br />
                  {t("ai_chat.how_package")}<br />
                  {t("ai_chat.how_final")} <code>globalEnabled && packageEnabled</code><br />
                  {t("ai_chat.how_user_note")}<br />
                  {t("ai_chat.how_off_note")}
                </Typography.Text>
              ),
            },
          ]}
        />

        <Tabs defaultActiveKey="user" items={tabItems} />
      </Space>
    </Card>
  );
};

export default AiChatSettings;
